import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../layouts/Layout";
import fileTextIcon from "../../assets/img3d/contrat.png";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator"; // Assurez-vous d'avoir ce composant ou remplacez par <hr />
import { toast } from "../../hooks/use-toast";
import {
  Mail,
  Phone,
  MapPin,
  Calendar,
  Clock,
  CheckCircle,
  Building2,
  MessageSquare,
  ArrowLeft,
  Home,
  User,
  Bot
} from "lucide-react";
import axios from "axios";
import { cn } from "@/lib/utils"; // Utilitaire pour les classes conditionnelles

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientRequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequestDetail();
  }, [id]);

  const fetchRequestDetail = async () => {
    try {
      const response = await axios.get(`${API}/client/my-requests/${id}`);
      const data = response.data;

      // Normalisation des données (identique à votre logique existante)
      const transformedRequest = {
        ...data.request,
        seeker_name: data.request.seekerName || data.request.seeker_name,
        seeker_email: data.request.seekerEmail || data.request.seeker_email,
        seeker_phone: data.request.seekerPhone || data.request.seeker_phone,
        seeker_message: data.request.seekerMessage || data.request.seeker_message,
        property_id: data.request.propertyId || data.request.property_id,
        budget_min: data.request.budgetMin ? Number(data.request.budgetMin) : null,
        budget_max: data.request.budgetMax ? Number(data.request.budgetMax) : null,
        move_in_date: data.request.moveInDate || data.request.move_in_date,
        visit_date: data.request.visitDate || data.request.visit_date,
        assignment_date: data.request.assignmentDate || data.request.assignment_date,
        created_at: data.request.createdAt || data.request.created_at,
        messages: data.request.messages || [],
        status: data.request.status || 'new',
        source: data.request.source || 'Plateforme'
      };

      setRequest(transformedRequest);
      setProperty(data.property);
    } catch (error) {
      console.error("Error fetching request detail:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les détails de la demande",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Palette statuts harmonisée avec la charte couleur
  const getStatusConfig = (status) => {
    const configs = {
      new: {
        label: "Nouveau",
        icon: Clock,
        badgeClass: "bg-orange-100 text-orange-700",
        chipClass: "text-orange-700",
      },
      qualified: {
        label: "En analyse",
        icon: Bot,
        badgeClass: "bg-indigo-100 text-indigo-700",
        chipClass: "text-indigo-700",
      },
      assigned: {
        label: "Assigné à un pro",
        icon: Building2,
        badgeClass: "bg-orange-100 text-orange-700",
        chipClass: "text-orange-700",
      },
      visit_planned: {
        label: "Visite programmée",
        icon: Calendar,
        badgeClass: "bg-amber-100 text-amber-700",
        chipClass: "text-amber-700",
      },
      completed: {
        label: "Terminé",
        icon: CheckCircle,
        badgeClass: "bg-emerald-100 text-emerald-700",
        chipClass: "text-emerald-700",
      },
      lost: {
        label: "Clôturé",
        icon: CheckCircle,
        badgeClass: "bg-gray-100 text-gray-700",
        chipClass: "text-gray-700",
      },
    };
    const key = (status || "new").toLowerCase();
    return configs[key] || configs.new;
  };

  if (loading) {
    return (
      <Layout headerConfig={{ icon: fileTextIcon, iconColor: "bg-white", title: "Chargement...", subtitle: "Patientez un instant" }}>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF9F68]"></div>
        </div>
      </Layout>
    );
  }

  if (!request) return null;

  const statusConfig = getStatusConfig(request.status);
  const StatusIcon = statusConfig.icon;

  return (
    <Layout
      headerConfig={{
        icon: fileTextIcon,
        iconColor: "bg-white",
        title: property?.title || "Ma demande",
        subtitle: `Créée le ${new Date(request.created_at).toLocaleDateString()}`,
        primaryAction: "Retour",
        onPrimaryAction: () => navigate("/client/my-requests"),
      }}
    >
      <div className="max-w-6xl mx-auto space-y-8 animate-in fade-in duration-500">
        
        {/* --- 1. BANDEAU DE STATUT --- */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-full ${statusConfig.badgeClass}`}>
              <StatusIcon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium uppercase tracking-wide">État actuel</p>
              <h2 className={`text-xl font-bold ${statusConfig.chipClass}`}>{statusConfig.label}</h2>
            </div>
          </div>
          
          <div className="flex gap-3">
             <Button 
              variant="outline" 
              className="rounded-xl border-gray-200 hover:bg-gray-50"
              onClick={() => navigate("/client/messages")}
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Messagerie
            </Button>
            {request.status === 'assigned' && (
               <Button className="bg-[#FF9F68] hover:bg-[#E67E4F] text-white rounded-xl shadow-lg shadow-orange-100">
                 Contacter le Pro
               </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* --- 2. COLONNE GAUCHE (Main Content) --- */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* Carte du Bien Immobilier - Design "Hero" */}
            {property && (
              <Card className="overflow-hidden border-none shadow-md rounded-2xl group">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={
                      (() => {
                        const firstImage = property.images?.[0];
                        const firstImageUrl =
                          typeof firstImage === "string"
                            ? firstImage
                            : firstImage?.url || firstImage?.secure_url || firstImage?.src;
                        return (
                          firstImageUrl ||
                          property.coverImage ||
                          property.thumbnail ||
                          property.image ||
                          property.image_url ||
                          property.imageUrl ||
                          property.property_image ||
                          property.property_image_url ||
                          property.main_image ||
                          property.mainImage ||
                          request?.propertyImage ||
                          request?.property_image ||
                          request?.property_image_url ||
                          "/placeholder-property.jpg"
                        );
                      })()
                    }
                    alt={property.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-6 text-white">
                    <Badge className="bg-[#FF9F68] hover:bg-[#FF9F68] border-none mb-2">Bien concerné</Badge>
                    <h3 className="text-2xl font-bold">{property.title}</h3>
                    <p className="flex items-center text-gray-200 mt-1">
                      <MapPin className="h-4 w-4 mr-1" />
                      {property.city}, {property.postalCode}
                    </p>
                  </div>
                </div>
                <CardContent className="p-6 flex justify-between items-center bg-white">
                  <div>
                    <p className="text-sm text-gray-500">Prix affiché</p>
                    <p className="text-2xl font-bold text-[#FF9F68]">{formatPrice(property.price)}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    className="text-[#FF9F68] hover:bg-orange-50"
                    onClick={() => window.open(`/properties/${property.id}`, "_blank")}
                  >
                    Voir la fiche complète <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Conversation Cynthia - Style Chat Moderne */}
            <Card className="border-gray-100 shadow-sm rounded-2xl overflow-hidden">
              <CardHeader className="bg-gray-50/50 border-b border-gray-100 pb-4">
                <div className="flex items-center gap-3">
                  
                  <div>
                    <CardTitle className="text-lg">Assistant Cynthia</CardTitle>
                    <p className="text-xs text-gray-500">Historique de qualification</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6 bg-white space-y-6">
                {request.messages && request.messages.length > 0 ? (
                  request.messages.map((msg, index) => {
                    const isAssistant = msg.author?.toLowerCase() === "assistant";
                    return (
                      <div key={index} className={`flex ${isAssistant ? "justify-start" : "justify-end"}`}>
                        <div className={`flex gap-3 max-w-[85%] ${isAssistant ? "flex-row" : "flex-row-reverse"}`}>
                       
                          <div>
                            <div
                              className={cn(
                                "p-4 rounded-2xl text-sm leading-relaxed shadow-sm",
                                isAssistant
                                  ? "bg-white border border-[#E2E8F0] text-[#2D3748] rounded-tl-none"
                                  : "bg-gradient-to-r from-[#FF9F68] to-[#EBC7A8] text-white rounded-tr-none"
                              )}
                            >
                              {msg.content}
                            </div>
                            <span className="text-[10px] text-gray-400 mt-1 block px-1">
                              {formatDate(msg.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                    <Bot className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Aucune conversation enregistrée.</p>
                  </div>
                )}
              </CardContent>
            </Card>

          </div>

          {/* --- 3. COLONNE DROITE (Sticky Sidebar) --- */}
          <div className="space-y-6">
            
            {/* Carte Infos Personnelles - Style "ID Card" */}
            <Card className="rounded-2xl shadow-sm border-gray-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="h-4 w-4 text-[#FF9F68]" /> Vos Coordonnées
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="bg-white p-2 rounded-lg shadow-sm">
                    <Mail className="h-4 w-4 text-gray-600" />
                  </div>
                  <div className="overflow-hidden">
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-sm font-medium truncate">{request.seeker_email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="bg-white p-2 rounded-lg shadow-sm">
                    <Phone className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Téléphone</p>
                    <p className="text-sm font-medium">{request.seeker_phone}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Carte Projet - Style "Résumé" */}
            <Card className="rounded-2xl shadow-sm border-gray-100 bg-gradient-to-br from-white to-gray-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Home className="h-4 w-4 text-[#FF9F68]" /> Détails du Projet
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {request.move_in_date && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Emménagement souhaité</p>
                    <div className="flex items-center gap-2 font-medium">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      {new Date(request.move_in_date).toLocaleDateString()}
                    </div>
                  </div>
                )}
                
                <Separator />
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Budget Min</p>
                    <p className="font-medium text-sm">{request.budget_min ? formatPrice(request.budget_min) : '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Budget Max</p>
                    <p className="font-medium text-sm">{request.budget_max ? formatPrice(request.budget_max) : '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Timeline Verticale Élégante - déplacée sous Détails du projet */}
            <Card className="rounded-2xl shadow-sm border-gray-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-[#FF9F68]" /> Suivi de la demande
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative pl-4 border-l-2 border-orange-100 ml-1 space-y-8 pb-2">
                  <div className="relative">
                    <div className="absolute -left-[21px] bg-orange-100 p-1 rounded-full ring-4 ring-white">
                      <div className="w-2 h-2 bg-[#FF9F68] rounded-full"></div>
                    </div>
                    <p className="text-sm font-semibold text-gray-900">Demande Créée</p>
                    <p className="text-xs text-gray-500">{formatDate(request.created_at)}</p>
                  </div>

                  {request.status !== "new" && (
                    <div className="relative">
                      <div className="absolute -left-[21px] bg-indigo-100 p-1 rounded-full ring-4 ring-white">
                        <div className="w-2 h-2 bg-indigo-600 rounded-full"></div>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">Qualification Terminée</p>
                      <p className="text-xs text-gray-500">Par l'assistant IA</p>
                    </div>
                  )}

                  {request.assignment_date && (
                    <div className="relative">
                      <div className="absolute -left-[21px] bg-orange-100 p-1 rounded-full ring-4 ring-white">
                        <div className="w-2 h-2 bg-[#FF9F68] rounded-full"></div>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">Assigné à un Pro</p>
                      <p className="text-xs text-gray-500">{formatDate(request.assignment_date)}</p>
                    </div>
                  )}

                  {request.visit_date && (
                    <div className="relative">
                      <div className="absolute -left-[21px] bg-amber-100 p-1 rounded-full ring-4 ring-white">
                        <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                      </div>
                      <div className="bg-amber-50 p-4 rounded-xl border border-amber-100 mt-1">
                        <p className="text-sm font-bold text-amber-800 flex items-center mb-1">
                          <Calendar className="h-4 w-4 mr-2" /> Visite Confirmée
                        </p>
                        <p className="text-sm text-amber-700">{formatDate(request.visit_date)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Carte Aide Rapide */}
            <div className="bg-blue-50 rounded-2xl p-5 border border-blue-100">
              <h4 className="font-semibold text-blue-900 mb-2 text-sm">Besoin d'aide ?</h4>
              <p className="text-xs text-blue-700 mb-3">
                Notre équipe support est disponible pour répondre à vos questions sur cette demande.
              </p>
              <Button variant="link" className="p-0 h-auto text-blue-600 text-xs font-semibold">
                Contacter le support →
              </Button>
            </div>

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ClientRequestDetailPage;